import { PartRelationshipEdge, PartRelationshipNode } from '@/api/generatedApi';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Background, Controls, Edge, MarkerType, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useMemo } from 'react';
import { EDGE_LABELS, edgeHandles, layoutRelationshipNodes, relationCaptions } from './relationshipGraphLayout';
import { RelationshipFlowNode, RelationshipNode } from './RelationshipNode';

const NODE_TYPES = { relationship: RelationshipNode };
const FIT_VIEW_OPTIONS = { maxZoom: 1 };

interface RelationshipGraphCanvasProperties {
  nodes: PartRelationshipNode[];
  edges: PartRelationshipEdge[];
  seedNodeId: string;
  hoveredEdgeId?: string;
  onHoverEdge: (edgeId?: string) => void;
  thumbnailsEnabled: boolean;
  onSelectNode: (node: PartRelationshipNode, position: { top: number; left: number }) => void;
}

export const RelationshipGraphCanvas = ({
  nodes,
  edges,
  seedNodeId,
  hoveredEdgeId,
  onHoverEdge,
  thumbnailsEnabled,
  onSelectNode,
}: RelationshipGraphCanvasProperties) => {
  const theme = useTheme();
  const positioned = useMemo(() => layoutRelationshipNodes(nodes, seedNodeId), [nodes, seedNodeId]);

  const flowNodes = useMemo<RelationshipFlowNode[]>(() => {
    const relations = relationCaptions(edges, seedNodeId);
    return positioned.map(({ node, x, y }) => ({
      id: node.id,
      type: 'relationship',
      position: { x, y },
      data: {
        node,
        isSeed: node.id === seedNodeId,
        showThumbnail: thumbnailsEnabled && !!node.hasThumbnail && !!node.reportId,
        relation: relations.get(node.id),
      },
    }));
  }, [positioned, edges, seedNodeId, thumbnailsEnabled]);

  const flowEdges = useMemo<Edge[]>(() => {
    const columns = new Map(positioned.map(({ node, column }) => [node.id, column]));
    return edges.map(edge => {
      const hovered = edge.id === hoveredEdgeId;
      const color = hovered ? theme.palette.primary.main : theme.palette.text.disabled;
      return {
        id: edge.id,
        source: edge.sourceNodeId,
        target: edge.targetNodeId,
        ...edgeHandles(columns.get(edge.sourceNodeId) ?? 0, columns.get(edge.targetNodeId) ?? 0),
        label: hovered ? EDGE_LABELS[edge.type] : undefined,
        interactionWidth: 16,
        style: { stroke: color, strokeWidth: hovered ? 2 : 1 },
        markerEnd: { type: MarkerType.ArrowClosed, color },
        zIndex: hovered ? 1 : 0,
      };
    });
  }, [edges, positioned, hoveredEdgeId, theme]);

  return (
    <Box
      sx={{ width: '100%', height: 520, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
      data-testid='relationship-graph-canvas'
    >
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={NODE_TYPES}
        colorMode={theme.palette.mode}
        nodesDraggable={false}
        nodesConnectable={false}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        minZoom={0.2}
        onEdgeMouseEnter={(_, edge) => onHoverEdge(edge.id)}
        onEdgeMouseLeave={() => onHoverEdge()}
        onNodeClick={(event, node: RelationshipFlowNode) =>
          onSelectNode(node.data.node, { top: event.clientY, left: event.clientX })
        }
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Box>
  );
};
